import React, { useEffect, useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Stack,
  Button,
  useTheme,
} from '@mui/material';
import { Profile } from '@par/shared/types/player';
import { GameType } from '../play-menu';
import { CategorySelect } from './CategorySelect';
import { TabLabel } from './TabLabel';
import { ToggleCasualPro } from './ToggleCasualPro';
import { TabPanel } from './TabPanel';
import { LensBlur, PriorityHigh } from '@mui/icons-material';

const a11yProps = (index: number) => {
  return {
    id: `solo-tab-${index}`,
    'aria-controls': `solo-tabpanel-${index}`,
  };
};

type PropTypes = {
  gameType: GameType;
  profile?: Profile;
  handleCasualProChange: (event: any, value: 'casual' | 'pro') => void;
  handleMultiplayerMode: (event: any, value: 'friendly' | 'ranked') => void;
  onCategoryBanClick: () => void;
  qualifiesForPunchcardPlay: (opCategory: string) => void;
  ranked: boolean;
  updatedCheckedCategories: (categories: any) => void;
  updatedCheckedTopCategories: (topCategories: any) => void;
};

export const PlayMenuTabsMultiplayer = ({
  gameType,
  profile,
  handleCasualProChange,
  handleMultiplayerMode,
  onCategoryBanClick,
  qualifiesForPunchcardPlay,
  ranked,
  updatedCheckedCategories,
  updatedCheckedTopCategories,
}: PropTypes) => {
  const [tabValue, setTabValue] = useState<'friendly' | 'ranked'>('friendly');
  const theme = useTheme();

  useEffect(() => {
    if (ranked) {
      setTabValue('ranked');
    } else {
      setTabValue('friendly');
    }
  }, [ranked]);

  return (
    <>
      <Box
        sx={{
          marginTop: '0px !important',
          backgroundColor: '#f9f9f90d',
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleMultiplayerMode}
          aria-label="multiplayer tabs"
          indicatorColor="secondary"
          centered
          sx={{ width: '100%', flex: 1 }}
        >
          <Tab
            label={
              <TabLabel text="Friendly" isActiveTab={tabValue === 'friendly'} />
            }
            {...a11yProps(0)}
            sx={{ flex: 1 }}
            value="friendly"
          />
          <Tab
            label={
              <TabLabel text="Ranked" isActiveTab={tabValue === 'ranked'} />
            }
            {...a11yProps(1)}
            sx={{ flex: 1 }}
            value="ranked"
          />
        </Tabs>
      </Box>
      <TabPanel value={ranked ? 1 : 0} index={0}>
        <ToggleCasualPro
          gameType={gameType}
          handleCasualProChange={handleCasualProChange}
        />
        <CategorySelect
          initCategorySelections={
            profile?.recentPlayMenuSettings?.categorySelections || []
          }
          qualifiesForPunchcardPlay={qualifiesForPunchcardPlay}
          updatedCheckedCategories={updatedCheckedCategories}
          updatedCheckedTopCategories={updatedCheckedTopCategories}
        />
      </TabPanel>
      <TabPanel value={ranked ? 1 : 0} index={1}>
        <Stack
          spacing={4}
          justifyContent={'center'}
          alignItems={'center'}
          py={1}
        >
          <Stack
            direction={'row'}
            px={2}
            py={1}
            sx={{
              border: '1px solid',
              borderColor: theme.palette.ranked.main,
              backgroundColor: theme.palette.ranked.main + '0d',
              borderRadius: 1.5,
            }}
          >
            <PriorityHigh sx={{ color: theme.palette.ranked.main }} />
            <Typography>
              Ranked is Pro difficulty across ALL categories
            </Typography>
            <PriorityHigh sx={{ color: theme.palette.ranked.main }} />
          </Stack>
          <Stack spacing={1}>
            <Stack
              direction={'row'}
              alignItems={'center'}
              justifyContent={'center'}
            >
              <LensBlur htmlColor={theme.palette.ranked.main} />
              <Typography>Boost your MMR with every win</Typography>
              <LensBlur htmlColor={theme.palette.ranked.main} />
            </Stack>
            <Stack
              direction={'row'}
              alignItems={'center'}
              justifyContent={'center'}
            >
              <LensBlur htmlColor={theme.palette.ranked.main} />
              Play 10 ranked matches to calibrate and earn a place on the
              leaderboard
              <LensBlur htmlColor={theme.palette.ranked.main} />
            </Stack>
            <Stack
              direction={'row'}
              alignItems={'center'}
              justifyContent={'center'}
            >
              <LensBlur htmlColor={theme.palette.ranked.main} />
              In ranked, you can ban a category
              <LensBlur htmlColor={theme.palette.ranked.main} />
            </Stack>
          </Stack>
          <Button
            variant="contained"
            color="primary"
            sx={{
              letterSpacing: 5,
            }}
            onClick={onCategoryBanClick}
          >
            Ban Category
          </Button>
        </Stack>
      </TabPanel>
    </>
  );
};
